import {ReactElement} from 'react';
import {I18n} from 'aws-amplify/utils';
import {Box} from '@mantine/core';
import {
    ReloadButton,
    SimplePageHeader,
} from '@flexinet/ui-components';

import {useZones} from '../hooks/useZones';
import {useZonesSelection} from '../hooks/useZonesSelection';
import {useZoneWriteCapabilities} from '../hooks/useZoneWriteCapabilities';
import {useZoneDeletion} from '../hooks/useZoneDeletion';
import {FilterRecordsButton} from '../buttons/FilterRecordsButton';
import {AddRecordButton} from '../buttons/AddRecordButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {DeleteZoneModal} from './DeleteZoneModal';
import {showErrorNotification} from '../../../components/ServiceNotifications';
import config from '../config';

const ZonesReloadButton = () => {
    const {reload} = useZones();
    return (
        <Box mx={'s'}>
            <ReloadButton onClick={() => reload()}/>
        </Box>
    );
};

// FNP-1191 CRUD half — the delete affordance, gated per selected zone.
//
// ⚠️ THE CAPABILITY IS RESOLVED FROM THE SELECTED ZONE'S OWN CONNECTION, not
// from "the tenant" — there is no such thing as a tenant-wide write capability
// here. Zones fan out across every connection, and a tenant can hold a writable
// Route 53 connection and a read-only Cloudflare one at the same time. Asking
// "can I delete THIS zone" is the only question with an answer.
//
// ⚠️ AND THE DEFAULT SURVIVES. `useZoneWriteCapabilities` returns false for
// canDeleteZones while loading, on error, with no snapshot, and for a
// connection that is not ACTIVE. Every one of those hides the button.
const DeleteZoneTool = () => {

    const {selectedRecords} = useZonesSelection();

    // One selected zone is the only state with an unambiguous connection. Zero
    // renders a disabled button; more than one is refused below.
    const selected = selectedRecords.length === 1 ? selectedRecords[0] : undefined;

    const {canDeleteZones} = useZoneWriteCapabilities(selected?.connectionId);

    const {
        zone,
        opened,
        requestDelete,
        cancelDelete,
        confirmDelete,
        isDeleting,
        error,
    } = useZoneDeletion();

    // ⚠️ HIDDEN, NOT DISABLED, when the credential cannot delete. A disabled
    // button promises a capability that does not exist; an absent one is a
    // correct statement about a read-only connection.
    //
    // The zero-selection case is the exception and stays VISIBLE-but-disabled:
    // with nothing selected there is no connection to ask about, and hiding the
    // button until a row is ticked makes the toolbar jump.
    const nothingSelected = selectedRecords.length === 0;
    if (!nothingSelected && !canDeleteZones) {
        return null;
    }

    const handleDelete = () => {
        // ⚠️ REFUSES A MULTI-ROW DELETE rather than fanning out. Every zone
        // carries its own refusal and its own delegation verdict, and a batch
        // reports one outcome for several — see hooks/useZoneDeletion.
        if (selectedRecords.length > 1) {
            showErrorNotification(
                I18n.get('Delete one zone at a time. Each zone has its own delegation state and its own provider refusal, and a batch would report one outcome for all of them.'),
            );
            return;
        }
        if (selected) {
            requestDelete(selected);
        }
    };

    return (
        <>
            <DeleteRecordButton
                count={selectedRecords.length}
                onClick={handleDelete}
            />
            <DeleteZoneModal
                zone={zone}
                opened={opened}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                isDeleting={isDeleting}
                error={error}
            />
        </>
    );
};

const useTools = (): ReactElement[] => {
    // FNP-1191 CRUD half: Add and Delete join Filter and Reload.
    //
    // Both write controls hide themselves on capability rather than being
    // conditionally rendered here — the decision belongs with the control that
    // knows which connection it is asking about.
    return [
        <AddRecordButton key={'add'}/>,
        <DeleteZoneTool key={'delete'}/>,
        <FilterRecordsButton key={'filter'}/>,
        <ZonesReloadButton key={'reload'}/>,
    ];
};

export const IndexPageHeader = () => {
    return (
        <SimplePageHeader
            title={I18n.get(config.titles.plural)}
            tools={useTools()}
        />
    );
};
